import { useMemo } from 'react';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { PredictionMarketConfigs } from 'src/hooks/predictionMarkets/predictionMarketConfigs';
import useDriftClient from 'src/hooks/useDriftClient';
import { useShallow } from 'zustand/react/shallow';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';

export default function usePortfolioOnChainMetrics() {
	const driftClient = useDriftClient();

	const [userId, userAuthority] = useDriftAccountStore(
		useShallow((s) => {
			const currentUserAccount = s.getCurrentUserAccount();
			return [currentUserAccount?.userId, currentUserAccount?.authority];
		})
	);

	const openPerpPositions = useCurrentAccountPositions();

	return useMemo(() => {
		if (userId === undefined || userAuthority === undefined || !driftClient) {
			return {
				currentUnrealizedPnl: undefined,
				currentCumulativeFunding: undefined,
			};
		}

		const nonPredictionMarketPositions = openPerpPositions?.filter(
			(position) =>
				!PredictionMarketConfigs.isPredictionMarket(position.marketSymbol)
		);

		const currentUnrealizedPnl = nonPredictionMarketPositions?.reduce(
			(acc, position) =>
				acc + BigNum.from(position.pnlVsMark, QUOTE_PRECISION_EXP).toNum(),
			0
		);

		const acct = driftClient.getUserAccount(userId, userAuthority);

		const currentCumulativeFunding = BigNum.from(
			acct?.cumulativePerpFunding,
			QUOTE_PRECISION_EXP
		).toNum();

		return {
			currentUnrealizedPnl,
			currentCumulativeFunding,
		};
	}, [openPerpPositions, userId, userAuthority, driftClient]);
}
